import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Relative } from '@/components/DateRangeFilter';
import { Title } from '@/components/Dropdown';
import { Input } from '@/components/Forms';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Tags } from '@/pages/AssignTopics/components/AttachmentsField';

import { StyledText } from './InvoicesFilterDropdown';
import { DropdownBox, DropdownButton } from './InvoicesFilterDropdown';

const Dropdown = styled.div`
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledInput = styled(Input)`
  height: 42px;
`;

const NotFoundPlaceholder = styled.div`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
`;

const StyledTitle = styled(Title)`
  margin-bottom: 12px;
  padding: 0;
`;

const StyledTags = styled(Tags)`
  margin-bottom: 0;
  margin-top: 12px;
`;

interface Props {
  options?: {
    name: string;
    value: string;
  }[];
  title: string;
  filter: boolean | string[];
  setFilter: React.Dispatch<React.SetStateAction<boolean | string[]>>;
}

const InvoicesCountriesDropdown: FC<Props> = ({
  options,
  title,
  filter,
  setFilter,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchName, setSearchName] = useState('');

  const [values, setValues] = useState<string[]>(
    Array.isArray(filter) ? filter : [],
  );

  const debouncedName = useDebounce(searchName, 400);

  const ref = useRef<HTMLInputElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  const renderCountryName = (countryCode: string) => {
    return options?.find((option) => option.value === countryCode)?.name;
  };

  const filteredOptions = options
    ?.filter((option) => !values.includes(option.value))
    .filter((option) =>
      debouncedName
        ? option.name.toLowerCase().includes(debouncedName.toLowerCase())
        : true,
    );

  useEffect(() => {
    setFilter(values);
  }, [values, setFilter]);

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedName, 'gi');
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  return (
    <Dropdown>
      <Relative>
        <StyledTitle $color="black" $type="smallBold">
          {title}
          <StyledText
            as="button"
            $color="gray"
            $type="extraSmall"
            disabled={!values.length}
            onClick={() => {
              setValues([]);
            }}
          >
            Clear
          </StyledText>
        </StyledTitle>
        <StyledInput
          placeholder="Select"
          ref={ref}
          onClick={() => setShowDropdown((prev) => !prev)}
          onChange={(e) => {
            setSearchName(e.target.value);
          }}
          value={searchName}
        />
        {showDropdown && (
          <DropdownBox>
            {!!filteredOptions?.length ? (
              <>
                {filteredOptions.map((option, index) => (
                  <DropdownButton
                    key={index}
                    type="button"
                    as="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      setValues([...values, option.value]);
                      setShowDropdown(false);
                    }}
                  >
                    <Text
                      $color="gray"
                      $type="captionSemibold"
                      dangerouslySetInnerHTML={{
                        __html: splitMatchedText(option.name),
                      }}
                    />
                  </DropdownButton>
                ))}
              </>
            ) : (
              <NotFoundPlaceholder>
                <Text $color="gray" $type="captionSemibold">
                  No Data Found
                </Text>
              </NotFoundPlaceholder>
            )}
          </DropdownBox>
        )}
      </Relative>
      {!!values.length && (
        <StyledTags>
          <>
            {values.map((value) => {
              return (
                <Tag
                  key={value}
                  onDelete={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setValues((prev) =>
                      prev.filter((prevValue) => prevValue !== value),
                    );
                  }}
                >
                  {renderCountryName(value)}
                </Tag>
              );
            })}
          </>
        </StyledTags>
      )}
    </Dropdown>
  );
};

export default InvoicesCountriesDropdown;
